import type { ReactNode } from 'react';
import type { Key } from 'react-aria-components';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { Flag05 } from '@untitledui/icons/Flag05';
import { Gift01 } from '@untitledui/icons/Gift01';
import { Luggage02 } from '@untitledui/icons/Luggage02';
import { Settings04 } from '@untitledui/icons/Settings04';
import { User01 } from '@untitledui/icons/User01';
import { camelCase } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { z } from 'zod';

import { Tabs } from '@/components/base/tabs/tabs';
import { profileQueryOptions } from '@/hooks/api';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import { getClient } from '@/utils/get-api-client';
import { getMessengerShareLink, getReferralLink } from '@/utils/referral-links';

import { AccountSettings } from './-components/account-settings/account-settings';
import { ProfessionalBackground } from './-components/professional-background';
import { ProfileInfo } from './-components/profile-info';
import { ProfileSocial } from './-components/profile-social';
import { Referrals } from './-components/referrals';

const settingsTabIds = [
  'profile-info',
  'professional-background',
  'social-accounts',
  'account-settings',
  'referrals',
] as const;

type SettingsTab = (typeof settingsTabIds)[number];

const settingsSearchSchema = z.object({
  tab: z.enum(settingsTabIds).catch('profile-info').default('profile-info'),
  /** Returned after core-api LinkedIn VoL OAuth callback redirects to settings */
  linkedin_vol: z.enum(['connected', 'error']).optional(),
  reason: z.string().optional(),
});

export type SettingsSearchParams = z.infer<typeof settingsSearchSchema>;

export const Route = createFileRoute('/participants/_authenticated/settings/')({
  validateSearch: settingsSearchSchema,
  beforeLoad: async ({ context }) => {
    const { queryClient, user } = context;

    await queryClient.ensureQueryData(
      profileQueryOptions(user.id, getClient(user.id)),
    );
  },
  component: SettingsPage,
});

function SettingsPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const analytics = useAnalytics();
  const currentTab = search.tab;
  const { user, profileId, env } = Route.useRouteContext();

  useEffect(() => {
    analytics?.screen({
      name: 'settings',
      attributes: { section: camelCase(currentTab) },
    });
  }, [analytics, currentTab]);

  const handleTabChange = useCallback(
    (tab: Key) => {
      void navigate({
        replace: true,
        search: (prev) => ({
          ...prev,
          tab: tab as SettingsTab,
        }),
      });
    },
    [navigate],
  );

  const referralLink = useMemo(
    () =>
      user.referralCode
        ? getReferralLink(env.WEBSITE_URL, user.referralCode)
        : undefined,
    [user.referralCode, env.WEBSITE_URL],
  );

  const messengerShareLink = useMemo(
    () =>
      referralLink
        ? getMessengerShareLink(env.FACEBOOK_APPID, referralLink)
        : undefined,
    [referralLink, env.FACEBOOK_APPID],
  );

  if (!user) {
    return <div>Error: User not found</div>;
  }

  const settingsTabs: {
    id: SettingsTab;
    leadingIcon: ReactNode;
    label: string;
    trailingIcon?: ReactNode;
  }[] = [
    {
      id: 'profile-info',
      leadingIcon: <User01 className="size-5" />,
      label: t`Profile info`,
    },
    {
      id: 'professional-background',
      leadingIcon: <Luggage02 className="size-5" />,
      label: t`Professional background`,
    },
    {
      id: 'social-accounts',
      leadingIcon: <Flag05 className="size-5" />,
      label: t`Social accounts`,
    },
    {
      id: 'account-settings',
      leadingIcon: <Settings04 className="size-5" />,
      label: t`Account settings`,
    },
    {
      id: 'referrals',
      leadingIcon: <Gift01 className="size-5" />,
      label: t`Referrals`,
    },
  ];

  return (
    <div className="mx-auto max-w-(--max-width-container) px-4 pt-4 pb-10 md:px-8 lg:pt-6 lg:pb-20">
      <Tabs
        orientation="vertical"
        className="flex flex-row lg:gap-2 xl:gap-8"
        selectedKey={currentTab}
        onSelectionChange={handleTabChange}
      >
        <div className="flex flex-col">
          <h1 className="mb-7 hidden pl-2 font-heading text-2xl font-semibold text-primary lg:block">
            <Trans>Settings</Trans>
          </h1>

          <Tabs.List
            type="button-gray"
            size="md"
            items={settingsTabs}
            className="hidden shrink-0 lg:mr-12 lg:flex"
          >
            {(tab) => (
              <Tabs.Item id={tab.id} className="pr-6 pb-2 pl-2">
                {tab.leadingIcon}
                {tab.label}
                {tab.trailingIcon}
              </Tabs.Item>
            )}
          </Tabs.List>
        </div>

        <Tabs.Panel id="profile-info" className="flex-1">
          <ProfileInfo userId={user.id} profileId={profileId} user={user} />
        </Tabs.Panel>
        <Tabs.Panel id="professional-background" className="flex-1">
          <ProfessionalBackground userId={user.id} profileId={profileId} />
        </Tabs.Panel>
        <Tabs.Panel id="social-accounts" className="flex-1">
          <ProfileSocial userId={user.id} profileId={profileId} />
        </Tabs.Panel>
        <Tabs.Panel id="account-settings" className="flex-1">
          <AccountSettings
            user={user}
            googleMapsApiKey={env.GOOGLE_BROWSER_APIKEY}
          />
        </Tabs.Panel>
        <Tabs.Panel id="referrals" className="flex-1">
          <Referrals
            userId={user.id}
            referralLink={referralLink}
            messengerShareLink={messengerShareLink}
          />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
}
